<template>
<v-dialog
  v-model="internalValue"
  max-width="760px"
  scrollable
  content-class="short-report-dialog"
>
  <v-card class="rounded-lg" style="overflow: hidden;">

    <!-- Dialog toolbar -->
    <div class="sr-toolbar">
      <span class="sr-toolbar__title">Короткий отчёт</span>
      <div class="d-flex align-center" style="gap: 8px;">
        <v-btn outlined small color="#009E60" class="sr-print-btn"
               :loading="pdfGenerating" :disabled="pdfGenerating"
               @click="$emit('generate-pdf')">
          <v-icon small left>mdi-file-pdf-box</v-icon>
          Скачать PDF
        </v-btn>
        <v-btn icon small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Report body -->
    <v-card-text id="short-report-body" class="pa-8" style="max-height: 75vh; overflow-y: auto; background: #fff;">

      <!-- Logo row -->
      <div class="sr-logo-row">
        <div class="sr-logo">
          <div class="sr-logo__avatar">
            <img src="@/assets/images/logoTA.svg" alt="">
          </div>
        </div>
        <span class="sr-logo-date">{{date}}</span>
      </div>

      <!-- Report type label -->
      <div class="sr-type-label">Короткий отчёт по управленческой диагностике</div>

      <!-- Participant name -->
      <h1 class="sr-name">{{candidateName}}</h1>
      <div class="sr-sub">{{position}} · {{company}}</div>

      <!-- Meta: expert | type | date -->
      <div class="sr-meta-row">
        <div class="sr-meta-col">
          <div class="sr-meta-col__label">ЭКСПЕРТ</div>
          <div class="sr-meta-col__value">{{manager || '—'}}</div>
        </div>
        <div class="sr-meta-col">
          <div class="sr-meta-col__label">ТИП</div>
          <div class="sr-meta-col__value">Управленческая диагностика</div>
        </div>
        <div class="sr-meta-col">
          <div class="sr-meta-col__label">ДАТА</div>
          <div class="sr-meta-col__value">{{date}}</div>
        </div>
      </div>

      <div class="sr-divider"/>

      <!-- Score cards -->
      <div class="sr-score-row">
        <div class="sr-score-card">
          <div class="sr-score-card__label">ОБЩИЙ БАЛЛ</div>
          <div class="sr-score-card__value">{{formattedScore}}</div>
        </div>
        <div class="sr-score-card">
          <div class="sr-score-card__label">ГРЕЙД</div>
          <div class="sr-score-card__value" :style="{ color: overallGradeColor }">{{overallGradeLabel}}</div>
        </div>
        <div class="sr-score-card">
          <div class="sr-score-card__label">ЗОНА</div>
          <div class="sr-score-card__value" :style="{ color: overallGradeColor }">{{overallZoneLabel}}</div>
        </div>
      </div>

      <div class="sr-divider"/>

      <!-- Competency profile table -->
      <h2 class="sr-section-heading">Профиль компетенций</h2>
      <table class="sr-table" style="margin-bottom:16px;">
        <thead>
        <tr>
          <th>КОМПЕТЕНЦИЯ</th>
          <th>БАЛЛ</th>
          <th>ГРЕЙД</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="comp in competencyRows" :key="comp.id">
          <td>{{comp.name}}</td>
          <td style="font-weight: 600;">{{comp.scoreFormatted}}</td>
          <td>
                <span
                  v-if="comp.score !== null"
                  class="sr-grade-badge"
                  :style="{ background: comp.gradeBg, color: comp.gradeColor }"
                >{{comp.gradeLabel}}</span>
            <span v-else style="color: #9e9e9e;">—</span>
          </td>
        </tr>
        </tbody>
      </table>

      <!-- Bar chart -->
      <div v-if="scoredCompetencies.length" class="sr-chart-wrap">
        <DiagnosticsCompetencyChart :competency-details="competencyRows" style="width:100%;height:100%;"/>
      </div>

      <div class="sr-divider"/>

      <!-- Assessor summary -->
      <h2 class="sr-section-heading">Итоги ассессора</h2>

      <!-- Strengths + Growth zones side by side -->
      <div class="sr-two-col">
        <div class="sr-two-col__cell">
          <div class="sr-col-label">СИЛЬНЫЕ СТОРОНЫ</div>
          <ul v-if="strengths.length" class="sr-list">
            <li v-for="(item, i) in strengths" :key="i">{{item}}</li>
          </ul>
          <p v-else class="sr-empty">Не указано</p>
        </div>
        <div class="sr-two-col__cell">
          <div class="sr-col-label">ЗОНЫ РОСТА</div>
          <ul v-if="growthAreas.length" class="sr-list">
            <li v-for="(item, i) in growthAreas" :key="i">{{item}}</li>
          </ul>
          <p v-else class="sr-empty">Не указано</p>
        </div>
      </div>

      <!-- Recommendations -->
      <div class="sr-col-label mt-5">РЕКОМЕНДАЦИИ</div>
      <div class="sr-block">
        <ul v-if="recommendations.length" class="sr-list">
          <li v-for="(item, i) in recommendations" :key="i">{{item}}</li>
        </ul>
        <p v-else class="sr-empty">Не указано</p>
      </div>

      <!-- Expert conclusion -->
      <div class="sr-col-label mt-5">КРАТКИЙ ВЫВОД ЭКСПЕРТА</div>
      <div class="sr-block sr-conclusion" v-html="safeExpertConclusion"/>

    </v-card-text>
  </v-card>
</v-dialog>
</template>

<script>
import DiagnosticsCompetencyChart from '@/components/Diagnostics/DiagnosticsCompetencyChart.vue';
import { getGrade } from '@/common/diagnosticGrades.js';
import { sanitizeRichHtml } from '@/common/util.js';

export default {
  name: 'ShortReportPreviewModal',

  components: { DiagnosticsCompetencyChart },

  model: {
    prop: 'value',
    event: 'input',
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    pdfGenerating: {
      type: Boolean,
      default: false,
    },
    candidateName: {
      type: String,
      default: '',
    },
    company: {
      type: String,
      default: '',
    },
    position: {
      type: String,
      default: '',
    },
    manager: {
      type: String,
      default: '',
    },
    date: {
      type: String,
      default: '',
    },
    overallScore: {
      type: Number,
      default: null,
    },
    competencyRows: {
      type: Array,
      default: () => [],
    },
    strengths: {
      type: Array,
      default: () => [],
    },
    growthAreas: {
      type: Array,
      default: () => [],
    },
    recommendations: {
      type: Array,
      default: () => [],
    },
    expertConclusion: {
      type: String,
      default: '',
    },
  },

  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit('input', v);
      },
    },

    formattedScore() {
      return this.overallScore !== null ? Number(this.overallScore)
        .toFixed(2) : '—';
    },

    safeExpertConclusion() {
      return sanitizeRichHtml(this.expertConclusion) || '—';
    },

    overallGradeLabel() {
      return getGrade(this.overallScore)?.label || '—';
    },

    overallZoneLabel() {
      const g = getGrade(this.overallScore);
      return g ? `${g.label}` : '—';
    },

    overallGradeColor() {
      return getGrade(this.overallScore)?.color || '#9e9e9e';
    },

    scoredCompetencies() {
      return this.competencyRows.filter(c => c.score !== null && c.score !== undefined);
    },
  },

  methods: {
    close() {
      this.$emit('input', false);
    },

  },
};
</script>

<style lang="scss">
.short-report-dialog {
  max-height: 90vh;
}
</style>

<style scoped lang="scss">
/* Toolbar */
.sr-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #e0e0e0;

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #212121;
  }
}

.sr-print-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  border-radius: 7px !important;
}

/* Logo row */
.sr-logo-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.sr-logo {
  display: flex;
  align-items: center;
  gap: 10px;

  &__avatar {
    border-radius: 8px;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 16px;
    flex-shrink: 0;
  }

  &__name {
    font-size: 15px;
    font-weight: 700;
    color: #212121;
  }
}

.sr-logo-date {
  font-size: 13px;
  color: #9e9e9e;
}

/* Report type label */
.sr-type-label {
  color: #009E60;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
  margin-bottom: 8px;
}

/* Participant */
.sr-name {
  font-size: 28px;
  font-weight: 700;
  color: #212121;
  margin: 0 0 4px;
  line-height: 1.2;
}

.sr-sub {
  font-size: 14px;
  color: #757575;
  margin-bottom: 20px;
}

/* Meta row */
.sr-meta-row {
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}

.sr-meta-col {
  &__label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .07em;
    color: #9e9e9e;
    margin-bottom: 4px;
  }

  &__value {
    font-size: 13px;
    font-weight: 600;
    color: #212121;
  }
}

/* Divider */
.sr-divider {
  height: 1px;
  background: #eeeeee;
  margin: 20px 0;
}

/* Score cards */
.sr-score-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}

.sr-score-card {
  flex: 1;
  min-width: 120px;
  border: 1px solid #eeeeee;
  border-radius: 8px;
  padding: 14px 16px;
  background: #fff;

  &__label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .07em;
    color: #9e9e9e;
    margin-bottom: 6px;
  }

  &__value {
    font-size: 20px;
    font-weight: 700;
    color: #212121;
    line-height: 1.2;
  }
}

/* Section heading */
.sr-section-heading {
  font-size: 17px;
  font-weight: 700;
  color: #212121;
  margin: 0 0 14px;
}

/* Competency table */
.sr-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    color: #9e9e9e;
    letter-spacing: .05em;
    padding: 10px 12px;
    border-bottom: 1.5px solid #eeeeee;
    background: #fafafa;
    white-space: nowrap;
  }

  td {
    padding: 10px 12px;
    border-bottom: 1px solid #f5f5f5;
    color: #424242;
    vertical-align: middle;
  }

  tr:last-child td {
    border-bottom: none;
  }
}

.sr-grade-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 9px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

/* Two-column assessor block */
.sr-two-col {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  &__cell {
    flex: 1;
    min-width: 180px;
    border: 1px solid #eeeeee;
    border-radius: 8px;
    padding: 14px 16px;
    background: #fafafa;
  }
}

.sr-col-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .07em;
  color: #9e9e9e;
  margin-bottom: 10px;
}

.sr-list {
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  color: #424242;
  line-height: 1.8;
}

.sr-block {
  border: 1px solid #eeeeee;
  border-radius: 8px;
  padding: 14px 16px;
  background: #fafafa;
  margin-top: 8px;
}

.sr-conclusion {
  font-size: 13px;
  color: #424242;
  line-height: 1.6;

  p {
    margin: 0 0 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.sr-empty {
  font-size: 13px;
  color: #9e9e9e;
  margin: 0;
}

.sr-chart-wrap {
  height: 220px;
  border: 1px solid #eeeeee;
  border-radius: 10px;
  padding: 16px 16px 8px;
  background: #fff;
  margin-bottom: 16px;
}

.mt-5 {
  margin-top: 20px;
}
</style>
